import { NotificationBubbleDot } from "@faire/deprecated-retailer-visitor-shared/components/Header/icons/NotificationBubbleDot";
import { Core } from "@faire/design-tokens";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import React from "react";
import styled, { css } from "styled-components";

const TRANSITION_SPEED_MS = "300ms";

interface IconWithMaybeDotProps {
  icon: React.ReactNode;
  hasHighPriority?: boolean;
  shouldAnimateDot?: boolean;
  shouldShowDot?: boolean;
  dataTestId?: string;
  dotBottom?: number;
  dotLeft?: number;
  isActive?: boolean;
  // Time to delay the animation of the dot in ms
  delayAnimationTime?: number;
}

export const IconWithMaybeDot: React.FC<IconWithMaybeDotProps> = ({
  icon,
  hasHighPriority,
  shouldAnimateDot,
  dataTestId,
  shouldShowDot,
  dotBottom,
  dotLeft,
  isActive,
  delayAnimationTime,
}) => {
  const isSSR = useIsSSR();

  return (
    <Row
      style={{
        width: "40px",
      }}
    >
      {!isSSR && shouldShowDot ? (
        <NotificationBubble
          $hasHighPriority={hasHighPriority}
          data-test-id={`${dataTestId}${hasHighPriority ? "-priority" : ""}`}
          $dotBottom={dotBottom}
          $dotLeft={dotLeft}
        >
          <NotificationBubbleDot
            hasHighPriority={hasHighPriority}
            shouldAnimate={shouldAnimateDot}
            delayAnimationTime={delayAnimationTime}
          />
        </NotificationBubble>
      ) : null}

      <IconWrapper $isActive={isActive} align="center" justify="center">
        {icon}
      </IconWrapper>
    </Row>
  );
};

const IconWrapper = styled(Row)<{ $isActive?: boolean }>`
  &:hover {
    background-color: ${Core.surface.subdued};
  }
  height: 40px;
  width: 40px;

  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${Core.surface.subdued};
        `
      : null}
  border-radius: 50%;
  transition: all ${TRANSITION_SPEED_MS} ease-out;
`;

const NotificationBubble = styled(Row)<{
  $hasHighPriority?: boolean;
  $dotBottom?: number;
  $dotLeft?: number;
}>`
  position: absolute;
  left: ${(props) =>
    props.$dotLeft
      ? css`
          ${props.$dotLeft}px
        `
      : "22px"};
  bottom: ${(props) =>
    props.$dotBottom
      ? css`
          ${props.$dotBottom}px
        `
      : "22px"};

  ${responsiveMediaQueries.desktopAndAbove} {
    left: ${(props) =>
      props.$dotLeft
        ? css`
            ${props.$dotLeft}px
          `
        : "18px"};
  }
  z-index: 1;
  &:hover + ${IconWrapper} {
    transition: all ${TRANSITION_SPEED_MS} ease-in;
    background-color: ${Core.surface.subdued};
    padding: ${getSpacing("1x")};
  }
`;
